import { ReactElement, useCallback, useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import {
  AUTH_CALLBACK_URL,
  EMAIL_REPORT_URL_PREFIX,
  LANDING_PAGE_URL,
  LOGIN_PAGE_URL,
  UNIVERSAL_LOGIN_PAGE_URL,
  TERMS_OF_USE_URL,
  REGISTER_URL,
  RESET_PASSWORD_URL,
  RESET_PASSWORD_SENT_URL,
  CONTACT_US_URL,
  REQUISITION_URL,
  MYPROFILE_URL,
  RELEASE_NOTES_URL,
  VIDEO_PAGE_URL,
} from "../utils/urlConstants";
import AuthCallback from "../pages/AuthCallback/AuthCallback";
import CreateAccountPage from "../pages/CreateAccount";
import LoginPage from "../pages/Login";
import UniversalLoginPage from "../pages/UniversalLogin";
import ResetPasswordPage from "../pages/ResetPassword";
import ResetPasswordSentPage from "../pages/ResetPasswordSent";
import { ProfilePage } from "../pages/Profile";
import ContactUsPage from "../pages/ContactUs";
import { Header } from "../components/Header/Header";
import { Footer } from "../components/Footer/Footer";
import { providerState } from "../recoil/atoms";
import { CustomGrid } from "../components/Grid/CustomGrid";
import ReportPage from "../pages/Report/ReportPage";
import { TermsOfServicePage } from "../components/TermsOfService/TermsOfService";
import ReleaseNotes from "../pages/ReleaseNotes";
import VideoPage from "../pages/Video/VideoPage";
import { ProtectedRoute } from "./ProtectedRoute";
import { HelpSidePanel } from "../pages/Help/HelpSidePanel";
import {
  ProviderAuthenticatedShell,
  ProviderShellBody,
  ProviderShellContent,
  ProviderShellHeader,
  ProviderShellHelpPanelSlot,
} from "./AppContainer.styles";
import { FeatureDiscoveryProvider } from "../components/FeatureDiscovery/FeatureDiscoveryProvider";
import { FeatureDiscoveryHost } from "../components/FeatureDiscovery/FeatureDiscoveryHost";

const AppContainer = (): ReactElement => {
  const { pathname } = useLocation();
  const providerInfo = useRecoilValue(providerState);
  const emailPrefix = pathname.startsWith(EMAIL_REPORT_URL_PREFIX);
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);

  const handleToggleHelpPanel = useCallback(() => {
    setIsHelpPanelOpen((currentIsOpen) => !currentIsOpen);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsHelpPanelOpen(false);
  }, [pathname]);

  const showHeaderAndFooter = Boolean(
    pathname !== LOGIN_PAGE_URL &&
    pathname !== UNIVERSAL_LOGIN_PAGE_URL &&
    pathname !== AUTH_CALLBACK_URL &&
    !pathname.startsWith(REGISTER_URL) &&
    !emailPrefix &&
    providerInfo?.token,
  );

  const routes = (
    <Switch>
      <Route exact path={LOGIN_PAGE_URL} component={LoginPage} />
      <Route
        exact
        path={UNIVERSAL_LOGIN_PAGE_URL}
        component={UniversalLoginPage}
      />
      <Route exact path={AUTH_CALLBACK_URL} component={AuthCallback} />
      <Route
        exact
        path={TERMS_OF_USE_URL}
        render={() => <TermsOfServicePage />}
      />
      <Route exact path={`${REGISTER_URL}`} component={CreateAccountPage} />
      <Route
        exact
        path={`${REGISTER_URL}/:jwtToken`}
        component={CreateAccountPage}
      />
      <Route exact path={RESET_PASSWORD_URL} component={ResetPasswordPage} />
      <Route
        exact
        path={RESET_PASSWORD_SENT_URL}
        component={ResetPasswordSentPage}
      />
      <Route exact path={MYPROFILE_URL} component={ProfilePage} />
      <Route exact path={CONTACT_US_URL} component={ContactUsPage} />
      <ProtectedRoute exact path={RELEASE_NOTES_URL} component={ReleaseNotes} />
      <ProtectedRoute exact path={VIDEO_PAGE_URL} component={VideoPage} />
      <Route exact path={`${REQUISITION_URL}/:sfID`} component={ReportPage} />
      <Route exact path={LANDING_PAGE_URL} component={CustomGrid} />
      <Redirect from="*" to={LANDING_PAGE_URL} />
    </Switch>
  );

  if (showHeaderAndFooter) {
    return (
      <FeatureDiscoveryProvider>
        <ProviderAuthenticatedShell
          data-testid="provider-authenticated-shell"
          data-help-panel-open={isHelpPanelOpen ? "true" : "false"}
        >
          <ProviderShellHeader>
            <Header
              isHelpPanelOpen={isHelpPanelOpen}
              onToggleHelpPanel={handleToggleHelpPanel}
            />
          </ProviderShellHeader>
          <ProviderShellBody $isHelpPanelOpen={isHelpPanelOpen}>
            <ProviderShellContent data-testid="provider-shell-content">
              {routes}
            </ProviderShellContent>
            <ProviderShellHelpPanelSlot
              $isHelpPanelOpen={isHelpPanelOpen}
              data-testid="provider-help-panel-slot"
            >
              <HelpSidePanel
                isOpen={isHelpPanelOpen}
                onClose={() => setIsHelpPanelOpen(false)}
              />
            </ProviderShellHelpPanelSlot>
          </ProviderShellBody>
          <Footer />
        </ProviderAuthenticatedShell>
        <FeatureDiscoveryHost />
      </FeatureDiscoveryProvider>
    );
  }

  return <>{routes}</>;
};
export default AppContainer;
